import { Component, OnInit, OnDestroy, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TableModule } from 'primeng/table';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { Tooltip } from 'primeng/tooltip';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { catchError, of } from 'rxjs';
import { Category } from '../../models/category.model';
import { CategoryService } from '../../services/category.service';
import { ServiceGroupService } from '../../services/service-group.service';
import { ServiceGroup } from '../../models/service-group.model';
import { AuthService } from '../../services/auth.service';
import { PageHeaderSearchActionService } from '../../services/page-header-search-action.service';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-category-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    Button,
    Dialog,
    Select,
    InputText,
    Tooltip,
    ConfirmDialog
  ],
  templateUrl: './category-management.component.html',
  styleUrl: './category-management.component.scss',
  providers: [ConfirmationService]
})
export class CategoryManagementComponent implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);
  categories: Category[] = [];
  filteredCategories: Category[] = [];
  selectedCategory: Category = this.getEmptyCategory();
  displayDialog = false;
  dialogMode: 'add' | 'edit' = 'add';
  loading = false;
  searchText: string = '';
  selectedStatusFilter: string | null = 'active';
  selectedServiceGroupFilter: string | null = null;
  serviceGroups: ServiceGroup[] = [];

  fieldTouched: Record<string, boolean> = {};

  rows: number = 50;
  first: number = 0;
  totalRecords: number = 0;
  rowsPerPageOptions: number[] = [5, 10, 20, 50];

  statusFilterOptions: { label: string; value: string | null }[] = [
    { label: 'All', value: null },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }
  ];

  get totalCategoriesCount(): number {
    return this.categories.length;
  }
  get activeCategoriesCount(): number {
    return this.categories.filter(c => c.isActive).length;
  }
  get inactiveCategoriesCount(): number {
    return this.categories.filter(c => !c.isActive).length;
  }

  constructor(
    private categoryService: CategoryService,
    private serviceGroupService: ServiceGroupService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private authService: AuthService,
    public pageHeaderService: PageHeaderSearchActionService
  ) {}

  ngOnInit() {
    this.pageHeaderService.setCanShowFilterToggle(true);
    this.pageHeaderService.setPlaceholder('Search by name or description...');
    this.pageHeaderService.setPrimaryButtonLabel('Add Category');
    this.pageHeaderService.setCanShowExport(true);
    this.pageHeaderService.setExportDisabled(true);
    this.searchText = this.pageHeaderService.currentSearchText;
    this.loadServiceGroups();
    this.loadCategories();
    this.pageHeaderService.searchText.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(v => {
      this.searchText = v ?? '';
      this.applySearch();
    });
    this.pageHeaderService.primaryActionClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.showAddDialog();
    });
    this.pageHeaderService.exportClick.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.exportToExcel();
    });
    this.authService.user$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      this.pageHeaderService.setCanShowPrimaryAction(!!(user?.isAdmin || user?.isPc));
      this.applySearch();
    });
  }

  ngOnDestroy(): void {
    this.pageHeaderService.clearPageConfig();
  }

  private loadServiceGroups(): void {
    this.serviceGroupService.getAllServiceGroups().subscribe({
      next: (groups) => {
        this.serviceGroups = (groups || []).filter(sg => sg.isActive === true);
      },
      error: () => {
        this.serviceGroups = [];
      }
    });
  }

  loadCategories() {
    this.loading = true;
    this.categoryService.getAllCategories().pipe(
      catchError(error => {
        console.error('Error loading categories:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load categories. Please refresh the page or try again later.'
        });
        this.loading = false;
        // Return empty array to prevent complete failure
        return of([] as Category[]);
      })
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        // Ensure isActive is set for all categories (default to true if missing)
        this.categories = data.map(c => ({
          ...c,
          isActive: c.isActive !== undefined ? c.isActive : true
        }));
        // Reapply filters to preserve filtered state
        this.applySearch();
        this.loading = false;
      }
    });
  }

  onSearch() {
    this.applySearch();
  }

  applySearch() {
    let filtered = [...this.categories];

    // Apply search text filter
    if (this.searchText.trim() !== '') {
      const searchLower = this.searchText.toLowerCase();
      filtered = filtered.filter(c =>
        c.name?.toLowerCase().includes(searchLower) ||
        c.description?.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (this.selectedStatusFilter !== null) {
      if (this.selectedStatusFilter === 'active') {
        filtered = filtered.filter(c => c.isActive === true);
      } else if (this.selectedStatusFilter === 'inactive') {
        filtered = filtered.filter(c => c.isActive === false);
      }
    }

    // Apply Service Group filter
    if (this.selectedServiceGroupFilter) {
      filtered = filtered.filter(c => c.serviceGroupId === this.selectedServiceGroupFilter);
    }

    this.filteredCategories = filtered;
    this.totalRecords = this.filteredCategories.length;
    this.first = 0;
    this.pageHeaderService.setExportDisabled(this.filteredCategories.length === 0);
  }

  clearSearch() {
    this.searchText = '';
    this.pageHeaderService.setSearchText('');
    this.applySearch();
  }

  onStatusFilterChange() {
    this.applySearch();
  }

  onServiceGroupFilterChange() {
    this.applySearch();
  }

  hasActiveFilters(): boolean {
    return !!(this.searchText.trim() || (this.selectedStatusFilter != null && this.selectedStatusFilter !== 'active') || this.selectedServiceGroupFilter);
  }

  clearFilters() {
    this.searchText = '';
    this.selectedStatusFilter = 'active'; // Reset to default 'active' view
    this.selectedServiceGroupFilter = null;
    this.applySearch();
  }

  toggleActiveStatus(category: Category) {
    const activating = !category.isActive;
    this.confirmationService.confirm({
      message: `Are you sure you want to ${activating ? 'activate' : 'deactivate'} "${category.name}"?`,
      header: activating ? 'Confirm Activate' : 'Confirm Deactivate',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: activating ? 'p-button-success' : 'p-button-danger',
      accept: () => {
        const updatedCategory = { ...category, isActive: !category.isActive };
        this.categoryService.updateCategory(category.id, updatedCategory).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: `Category ${updatedCategory.isActive ? 'activated' : 'deactivated'} successfully`
            });
            const index = this.categories.findIndex(c => c.id === category.id);
            if (index !== -1) {
              this.categories = [...this.categories];
              this.categories[index] = { ...this.categories[index], isActive: updatedCategory.isActive };
            }
            this.applySearch();
          },
          error: (error) => {
            console.error('Error updating category status:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to update category status'
            });
          }
        });
      }
    });
  }

  showAddDialog() {
    this.selectedCategory = this.getEmptyCategory();
    this.fieldTouched = {};
    this.dialogMode = 'add';
    this.displayDialog = true;
  }

  showEditDialog(category: Category) {
    this.selectedCategory = { ...category };
    this.fieldTouched = {};
    this.dialogMode = 'edit';
    this.displayDialog = true;
  }

  hideDialog() {
    this.displayDialog = false;
    this.selectedCategory = this.getEmptyCategory();
    this.fieldTouched = {};
  }

  resetCategoryForm() {
    this.selectedCategory = this.getEmptyCategory();
    this.fieldTouched = {};
  }

  saveCategory() {
    // Mark all required fields as touched
    this.fieldTouched['name'] = true;

    if (!this.selectedCategory.name.trim()) {
      return;
    }

    if (this.dialogMode === 'add') {
      this.categoryService.addCategory(this.selectedCategory).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (created) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Category added successfully'
          });
          const newCategory = { ...created, isActive: created.isActive !== undefined ? created.isActive : true };
          this.categories = [...this.categories, newCategory];
          this.applySearch();
          this.hideDialog();
        },
        error: (error) => {
          console.error('Error adding category:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to add category'
          });
        }
      });
    } else {
      this.categoryService.updateCategory(this.selectedCategory.id, this.selectedCategory).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (updated) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Category updated successfully'
          });
          const index = this.categories.findIndex(c => c.id === updated.id);
          if (index !== -1) {
            this.categories = [...this.categories];
            this.categories[index] = { ...updated, isActive: updated.isActive !== undefined ? updated.isActive : true };
          }
          this.applySearch();
          this.hideDialog();
        },
        error: (error) => {
          console.error('Error updating category:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to update category'
          });
        }
      });
    }
  }

  exportToExcel(): void {
    if (!this.filteredCategories?.length) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Data',
        detail: 'No categories to export'
      });
      return;
    }
    try {
      const exportData = this.filteredCategories.map(c => ({
        'Name': c.name || '',
        'Description': (c.description || '').slice(0, 200),
        'Service Group': c.serviceGroupName || '',
        'Status': c.isActive ? 'Active' : 'Inactive'
      }));
      const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
      worksheet['!cols'] = [{ wch: 22 }, { wch: 36 }, { wch: 18 }, { wch: 10 }];
      const workbook: XLSX.WorkBook = {
        Sheets: { 'Categories': worksheet },
        SheetNames: ['Categories']
      };
      const excelBuffer: unknown = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer as BlobPart], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(data, `Categories_${this.formatDateForFile(new Date())}.xlsx`);
      this.messageService.add({
        severity: 'success',
        summary: 'Success',
        detail: 'Categories exported to Excel successfully'
      });
    } catch (error) {
      console.error('Error exporting categories:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to export categories to Excel'
      });
    }
  }

  private formatDateForFile(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}${m}${d}`;
  }

  private getEmptyCategory(): Category {
    return {
      id: '',
      name: '',
      description: '',
      isActive: true,
      serviceGroupId: null,
      serviceGroupName: null
    };
  }

  onServiceGroupChange(): void {
    if (this.selectedCategory.serviceGroupId) {
      const serviceGroup = this.serviceGroups.find(sg => sg.id === this.selectedCategory.serviceGroupId);
      this.selectedCategory.serviceGroupName = serviceGroup?.name || null;
    } else {
      this.selectedCategory.serviceGroupName = null;
    }
  }
}

